/**
 * lib/pult-mixer.ts — the pult's mixer in numbers: where a knob or a fader stands → what the
 * deck's chain of sound is set to (task 56).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Цепочка звука деки, микшер, автомат». Pure:
 * positions in (0…1, as `store/pult.ts` holds them), numbers out. `engine/mixer.ts` writes them
 * into the nodes; `test/pult-mixer.test.ts` pins them.
 *
 * ## The chain of a deck
 *
 * element → LOW (`lowshelf` 100 Hz) → MID (`peaking` 1 kHz, Q 0,7) → HIGH (`highshelf` 8 kHz) →
 * FILTER → the deck's gain → MAIN. Each tone knob is −26 … +6 dB, 0 in the middle: a DJ mixer cuts
 * deep and boosts a little. FILTER is one knob for two filters: its left half a `lowpass` sweeping
 * down from 20 kHz to 200 Hz, its right half a `highpass` sweeping up from 20 Hz to 5 kHz, the
 * middle off. Frequencies go by the ear's own scale — evenly in octaves, not in hertz.
 *
 * ## The deck's gain
 *
 * `LEVEL × fader × crossfader(deck)`. LEVEL is the tone knobs' scale, 0 dB in the middle; a fader
 * goes down by the cosine of its travel — half way is a little quieter, not half as loud; the
 * crossfader is equal power, the very curve of the overlap of task 45 ({@link equalPower}), so the
 * middle is as loud as either end. With every control at rest — LEVEL in the middle, faders up, the
 * crossfader at the active deck's side — the deck playing is at exactly 1 and the other at 0: the
 * player sounds as it did before the mixer.
 */

import type { DeckSide } from './pult-layout';
import { equalPower } from './transition';

/** A tone knob's reach: down to this many dB… */
export const TONE_CUT_DB = -26;
/** …and up to this many. */
export const TONE_BOOST_DB = 6;

/** The three tone knobs, and the filter each is. */
export const TONE_BANDS = Object.freeze({
  low: { type: 'lowshelf', frequency: 100, q: 1 },
  mid: { type: 'peaking', frequency: 1000, q: 0.7 },
  high: { type: 'highshelf', frequency: 8000, q: 1 },
} as const);

export type ToneBand = keyof typeof TONE_BANDS;

/** FILTER's left half sweeps a lowpass from here… */
export const LOWPASS_OPEN_HZ = 20_000;
/** …down to here. */
export const LOWPASS_SHUT_HZ = 200;
/** Its right half a highpass from here… */
export const HIGHPASS_OPEN_HZ = 20;
/** …up to here. */
export const HIGHPASS_SHUT_HZ = 5_000;
/**
 * The filters' Q. Web Audio reads a lowpass's and a highpass's Q as a resonance in dB: −3,01 dB is
 * the linear 1/√2 — a plain slope, no peak at the cutoff.
 */
export const FILTER_Q = -3.01;
/** Within this of the middle, FILTER is off. */
export const FILTER_REST = 0.02;

function within(value: number): number {
  return Number.isFinite(value) ? Math.min(1, Math.max(0, value)) : 0.5;
}

/** A knob of the tone scale at `position`, in dB: −26 at the left, 0 in the middle, +6 at the right. */
export function knobDb(position: number): number {
  const at = within(position);
  return at < 0.5 ? TONE_CUT_DB * (1 - 2 * at) : TONE_BOOST_DB * (2 * at - 1);
}

/** dB as a gain. */
export function dbGain(db: number): number {
  return 10 ** (db / 20);
}

/** A tone knob as its filter's settings. */
export function toneSettings(
  band: ToneBand,
  position: number,
): {
  readonly type: string;
  readonly frequency: number;
  readonly q: number;
  readonly gainDb: number;
} {
  const { type, frequency, q } = TONE_BANDS[band];
  return { type, frequency, q, gainDb: knobDb(position) };
}

/** `from` to `to` in octaves, at `share` of the way. */
function sweep(from: number, to: number, share: number): number {
  return from * (to / from) ** share;
}

/**
 * FILTER at `position` — see «The chain of a deck». In the middle a lowpass at 20 kHz, the top of
 * hearing: off to the ear, though not a bypass.
 */
export function filterSettings(position: number): {
  readonly type: 'lowpass' | 'highpass';
  readonly frequency: number;
  readonly q: number;
  readonly on: boolean;
} {
  const at = within(position);
  if (Math.abs(at - 0.5) < FILTER_REST) {
    return { type: 'lowpass', frequency: LOWPASS_OPEN_HZ, q: FILTER_Q, on: false };
  }
  if (at < 0.5) {
    const share = (0.5 - at) / 0.5;
    return {
      type: 'lowpass',
      frequency: sweep(LOWPASS_OPEN_HZ, LOWPASS_SHUT_HZ, share),
      q: FILTER_Q,
      on: true,
    };
  }
  const share = (at - 0.5) / 0.5;
  return {
    type: 'highpass',
    frequency: sweep(HIGHPASS_OPEN_HZ, HIGHPASS_SHUT_HZ, share),
    q: FILTER_Q,
    on: true,
  };
}

/** LEVEL as a gain: the tone scale, 0 dB in the middle. Fully down is silence. */
export function levelGain(position: number): number {
  const at = within(position);
  return at === 0 ? 0 : dbGain(knobDb(at));
}

/** A channel fader as a gain: `position` is the share of its slot from the top — up is full. */
export function faderGain(position: number): number {
  const at = within(position);
  return at >= 1 ? 0 : Math.cos((at * Math.PI) / 2);
}

/** The crossfader at `position` — 0 deck A's side, 1 deck B's — as the two decks' gains. */
export function crossfaderGains(position: number): Readonly<Record<DeckSide, number>> {
  const at = within(position);
  // At either end the other deck is exactly silent: `cos(π/2)` is 6·10⁻¹⁷, not 0.
  if (at <= 0) return { A: 1, B: 0 };
  if (at >= 1) return { A: 0, B: 1 };
  const { out, in: into } = equalPower(at);
  return { A: out, B: into };
}

/** The crossfader's side of a deck: where it stands with only that deck heard. */
export function sideOfDeck(side: DeckSide): 0 | 1 {
  return side === 'A' ? 0 : 1;
}

export interface MixerPositions {
  readonly level: Readonly<Record<DeckSide, number>>;
  readonly fader: Readonly<Record<DeckSide, number>>;
  readonly crossfader: number;
}

/** Every deck's gain: `LEVEL × fader × crossfader(deck)`. */
export function deckGains(positions: MixerPositions): Readonly<Record<DeckSide, number>> {
  const cross = crossfaderGains(positions.crossfader);
  const gain = (side: DeckSide): number =>
    levelGain(positions.level[side]) * faderGain(positions.fader[side]) * cross[side];
  return { A: gain('A'), B: gain('B') };
}

/**
 * The deck the mixer makes the louder — fader × crossfader, LEVEL aside (the design's rule: LEVEL
 * is the trim of a track, not the choice of a deck); `undefined` on a tie, which keeps the deck it
 * was.
 */
export function louderDeck(positions: MixerPositions): DeckSide | undefined {
  const cross = crossfaderGains(positions.crossfader);
  const a = faderGain(positions.fader.A) * cross.A;
  const b = faderGain(positions.fader.B) * cross.B;
  if (Math.abs(a - b) < 1e-9) return undefined;
  return a > b ? 'A' : 'B';
}
